import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Check, Info, AlertCircle } from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * 36×36 circular status indicator.
 * - `load`    — animated spinner (brand colour)
 * - `success` — filled ring + checkmark
 * - `warning` — filled ring + info icon
 * - `error`   — filled ring + exclamation icon
 */
const loaderVariants = cva(
	'relative inline-flex size-9 shrink-0 items-center justify-center rounded-full border-[3px]',
	{
		variants: {
			state: {
				load:    'border-(--color-brand-light) border-t-(--color-brand) animate-spin',
				success: 'border-(--color-success)',
				warning: 'border-(--color-warning)',
				error:   'border-(--color-destructive)',
			},
		},
		defaultVariants: { state: 'load' },
	}
)

type LoaderState = 'load' | 'success' | 'warning' | 'error'

const iconColorByState: Record<Exclude<LoaderState, 'load'>, string> = {
	success: 'text-(--color-success)',
	warning: 'text-(--color-warning)',
	error:   'text-(--color-destructive)',
}

export interface LoaderProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof loaderVariants> {
	/** Optional custom icon overriding the default state icon. */
	icon?: React.ReactNode
}

const DefaultIcon = ({ state }: { state: Exclude<LoaderState, 'load'> }) => {
	const className = cn('size-5', iconColorByState[state])
	if (state === 'success') return <Check className={className} strokeWidth={2.5} aria-hidden />
	if (state === 'warning') return <Info className={className} strokeWidth={2.5} aria-hidden />
	return <AlertCircle className={className} strokeWidth={2.5} aria-hidden />
}

const Loader = React.forwardRef<HTMLDivElement, LoaderProps>(
	({ className, state = 'load', icon, role, 'aria-label': ariaLabel, ...props }, ref) => (
		<div
			ref={ref}
			role={role ?? (state === 'load' ? 'status' : 'img')}
			aria-label={ariaLabel ?? (state === 'load' ? 'Loading' : undefined)}
			className={cn(loaderVariants({ state, className }))}
			{...props}
		>
			{state && state !== 'load' && (
				<span className="absolute inset-0 flex items-center justify-center">
					{icon ?? <DefaultIcon state={state as Exclude<LoaderState, 'load'>} />}
				</span>
			)}
		</div>
	)
)

Loader.displayName = 'Loader'
export { Loader, loaderVariants }
